export const DEFAULT_REGISTRY = 'docker.io';

export interface DockerImage {
  registry: string;
  repository: string;
  tag: string;
}

export interface DockerImageWithDigest extends DockerImage {
  digest: string;
}

export const urlRegexp = new RegExp(
  '^' +
    // protocol identifier
    '(?:(?:https?|ftp)://)?' +
    // user:pass authentication
    '(?:\\S+(?::\\S*)?@)?' +
    '(?:' +
    // IP address exclusion
    // private & local networks
    // '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
    // '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
    // '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
    // IP address dotted notation octets
    // excludes loopback network 0.0.0.0
    // excludes reserved space >= 224.0.0.0
    // excludes network & broacast addresses
    // (first & last IP address of each class)
    '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
    '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
    '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
    '|' +
    // host name
    '(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)' +
    // domain name
    '(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*' +
    // TLD identifier
    '(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))' +
    // TLD may end with dot
    '\\.?' +
    ')' +
    // port number
    '(?::\\d{2,5})?' +
    // resource path
    '(?:[/?#]\\S*)?' +
    '$',
  'i',
);

/**
 * Parses a Docker image reference (registry/repository:tag[@digest]) into its components
 * @param imageRef - The Docker image reference string to parse
 * @returns Parsed DockerImageWithDigest object or undefined if parsing fails
 */
export const parseDockerImageReference = (
  imageRef?: string,
): DockerImageWithDigest | undefined => {
  if (!imageRef || typeof imageRef !== 'string') {
    return undefined;
  }

  // Extract digest, if present
  const mainParts = imageRef.split('@');
  const digest = mainParts.length > 1 ? mainParts[1] : '';
  const mainPart = mainParts[0];

  // Extract registry, repository and tag
  let registry: string;
  let repository: string;
  let tag: string;
  const parts = mainPart.split('/');

  if (parts.length === 1) {
    // Case: centos:latest
    registry = DEFAULT_REGISTRY;
    [repository, tag] = mainPart.split(':');
  } else if (urlRegexp.test(parts[0])) {
    // Case: docker.io/ubuntu:latest or quay.io/centos/centos:latest
    registry = parts[0];
    if (parts.length === 2) {
      [repository, tag] = parts[1].split(':');
    } else {
      [repository, tag] = parts.slice(1).join('/').split(':');
    }
  } else if (parts.length === 2) {
    // Case: library/ubuntu:latest or quay.io/centos:latest
    registry = DEFAULT_REGISTRY;
    [repository, tag] = parts[1].split(':');
    repository = `${parts[0]}/${repository}`;
  } else if (parts.length === 3) {
    // Case: docker.io/ubuntu:latest
    registry = parts[0];
    [repository, tag] = parts[2].split(':');
    repository = `${parts[1]}/${repository}`;
  } else if (parts.length > 3) {
    // Case: docker.io:8080/library/ubuntu:latest
    registry = parts.slice(0, parts.length - 2).join('/');
    [repository, tag] = parts[parts.length - 1].split(':');
    repository = `${parts[parts.length - 2]}/${repository}`;
  } else {
    // Default case
    registry = DEFAULT_REGISTRY;
    [repository, tag] = mainPart.split(':');
  }

  // Validate extracted parts — tag and digest may legitimately be absent
  // (e.g. bare repo names like "alpine"), so only registry + repository are
  // required.
  if (!registry || !repository) {
    return undefined;
  }

  return {
    registry,
    repository,
    tag: tag ?? '',
    digest: digest ?? '',
  };
};

/**
 * The docker pull string contains the registry, repository, and tag and most
 * registries will provide a way of copying it directly. This function extracts
 * the elements from it that can be used to populate the form (if possible)
 * @param pullString - The docker pull command string to parse
 * @returns Parsed DockerImageWithDigest object or false if parsing fails
 */
export const parseDockerPullString = (pullString: string) => {
  // Remove extraneous items and extract the main part of the pull string
  const imageRef = pullString
    .split(/\s+/)
    .filter(part => part.includes('/') || part.includes(':'))
    .pop();

  if (!imageRef) {
    return false;
  }

  return parseDockerImageReference(imageRef);
};

/**
 * Builds a Docker image link URL for the artifacts section
 * @param image - Partial DockerImageWithDigest object containing registry, repository, tag, and/or digest
 * @param level - Either 'digest' (include digest if available) or 'tag' (stop at tag level). Defaults to 'digest'.
 * @returns The constructed URL path or null if insufficient parameters (registry, repository, tag required)
 */
export const buildDockerImageHref = (
  image: Partial<DockerImageWithDigest>,
  level: 'digest' | 'tag' = 'digest',
): string | null => {
  const { registry, repository, tag, digest } = image;

  if (!registry || !repository || !tag) {
    return null;
  }

  const baseUrl = `/artifacts/image/${registry}/${encodeURIComponent(repository)}/${tag}`;

  // Add digest if level is 'digest' and digest is available
  return level === 'digest' && digest ? `${baseUrl}/${digest}` : baseUrl;
};
